import { Injectable } from '@angular/core';
import {
	Data$,
	Notification,
	RemoteData,
	appEntities,
	appEntity,
	catchErrorTo,
	filterNil,
	switchTap,
} from '@lycoris-nubila/ngx-lycoris';
import { TranslateService } from '@ngx-translate/core';
import uniqBy from 'lodash-es/uniqBy';
import { BehaviorSubject, Observable, combineLatest, from, of } from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	map,
	mapTo,
	pluck,
	shareReplay,
	startWith,
	switchMap,
	switchMapTo,
	take,
	tap,
} from 'rxjs/operators';
import { CacheObservable } from 'src/app/app-decorators';

import { ApplicationAdapter } from 'application/application.adapter';
import { CommandEvent } from 'application/common/model/command-event';
import { NotificationDomain } from 'application/common/model/notification-domain';
import { AppPushApiPort } from 'application/common/port/app-push-api.port';
import { DocumentConverterPort } from 'document/port/document-converter.port';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { UserModuleAnswerOutput } from 'infrastructure/graphql/content/user/model/user-module-answer.output';
import { UserModuleStatisticOutput } from 'infrastructure/graphql/content/user/model/user-module-statistic.output';
import { UserModuleOutput } from 'infrastructure/graphql/content/user/model/user-module.output';
import { UserPhaseStatsOutput } from 'infrastructure/graphql/content/user/model/user-phase-stats.output';
import { MessagingUserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/messaging-user-module-push.output';
import { UserModulePushOutput } from 'infrastructure/graphql/messaging/user/model/user-module-push.output';
import { ModuleQuestionConverterPort } from 'module/page/question/port/module-question-converter.port';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { Organization } from 'organization/organization';
import { OrganizationConverterPort } from 'organization/port/organization-converter.port';
import { OrganizationRepositoryPort } from 'organization/port/organization-repository.port';
import { ConnectToUserNetworkCmd } from 'user/command/connect-to-user-network.cmd';
import { MarkUserModuleDescriptionAsVisualizedCmd } from 'user/command/mark-user-module-description-as-visualized.cmd';
import { ReconnectToUserNetworkCmd } from 'user/command/reconnect-to-user-network.cmd';
import { SynchronizeUserNetworkSelectedCalendarsCmd } from 'user/command/synchronize-user-network-selected-calendars.cmd';
import { Auth0ConnectionOutput } from 'user/model/auth0-connection.output';
import { ExternalCalendarInformationOutput } from 'user/model/external-calendar-information.output';
import { StatisticsUserOutput } from 'user/model/statistics-user.output';
import { UserAvailableSlotOutput } from 'user/model/user-available-slot.output';
import { UserMfaEngageOutput } from 'user/model/user-mfa-engage.output';
import { UserNetworkState } from 'user/model/user-network.state';
import { UserNetworkType } from 'user/model/user-network.type';
import { UserNoteOutput } from 'user/model/user-note.output';
import { UserOutput } from 'user/model/user.output';
import { IUserNote } from 'user/note/user-note';
import { UserEmailModelRepositoryPort } from 'user/port/email/user-email-model-repository.port';
import { UserModuleAnswerRepositoryPort } from 'user/port/module/answer/user-module-answer-repository.port';
import { UserModuleRepositoryPort } from 'user/port/module/user-module-repository.port';
import { UserNoteRepositoryPort } from 'user/port/note/user-note-repository.port';
import { UserModuleSuggestionRepositoryPort } from 'user/port/suggestion/user-module-suggestion-repository.port';
import { UserAnalyticsPort } from 'user/port/user-analytics.port';
import { UserApiPort } from 'user/port/user-api.port';
import { UserApplicationPort } from 'user/port/user-application.port';
import { UserSessionPort } from 'user/port/user-session.port';
import { FetchOwnedUserModulePushesQuery } from 'user/query/fetch-owned-user-module-pushes.query';
import { FetchUserAvailableSlotsQuery } from 'user/query/fetch-user-available-slots.query';
import { ObserveUserAnswersByModulesIdQuery } from 'user/query/module/answer/observe-user-answers-by-modules-id.query';
import { ObserveUserAnswersByQuestionsIdQuery } from 'user/query/module/answer/observe-user-answers-by-questions-id.query';
import { ObserveSearchedUserCoachNotesQuery } from 'user/query/note/observe-searched-user-coach-notes.query';
import { ObserveSearchedCoachesIncludingPartnersQuery } from 'user/query/observe-searched-coaches-including-partners.query';
import { IUserModuleSuggestion } from 'user/suggestion/user-module-suggestion';
import { IUser, User } from 'user/user';
import { UserConverterService } from 'user/user-converter.service';
import { IUserEmailModel } from 'user/user-email-model';
import { UserEmailModelConverterService } from 'user/user-email-model-converter.service';
import { IUserModule } from 'user/user-module';
import { IUserModuleAnswer } from 'user/user-module-answer';
import { UserModuleAnswerConverterService } from 'user/user-module-answer-converter.service';
import { UserModuleConverterService } from 'user/user-module-converter.service';
import { UserModulePush } from 'user/user-module-push';
import { UserModulePushConverterService } from 'user/user-module-push-converter.service';
import { UserModuleSuggestionConverterService } from 'user/user-module-suggestion-converter.service';
import { UserNoteConverterService } from 'user/user-note-converter.service';
import { IUserSettings } from 'user/user-settings';
import { UserSettingsConverterService } from 'user/user-settings-converter.service';

import { AppLocator } from '../../app.locator';
import { AssociateCoachWithCoacheeCmd } from './command/associate-coach-with-coachee.cmd';
import { CreateUserCmd } from './command/create-user.cmd';
import { GenerateUserCVCmd } from './command/generate-user-cv.cmd';
import { AddModuleToUserPlanCmd } from './command/module/add-module-to-user-plan.cmd';
import { CreateUserModuleAnswersDocumentCmd } from './command/module/answer/create-user-module-answers-document.cmd';
import { DeleteUserModuleAnswersCmd } from './command/module/answer/delete-user-module-answers.cmd';
import { GenerateUserModuleAnswersCmd } from './command/module/answer/generate-user-module-answers.cmd';
import { SaveUserAnswersCmd } from './command/module/answer/save-user-answers.cmd';
import { DeleteModuleFromUserPlanCmd } from './command/module/delete-module-from-user-plan.cmd';
import { ResendUserModulePushCmd } from './command/module/push/resend-user-module-push.cmd';
import { UpdateUserModuleCoachCommentCmd } from './command/module/update-user-module-coach-comment.cmd';
import { UpdateUserModuleRatingCmd } from './command/module/update-user-module-rating.cmd';
import { UpdateUserModuleCmd } from './command/module/update-user-module.cmd';
import { CreateUserNoteCmd } from './command/note/create-user-note.cmd';
import { SaveUserModuleSpentDurationCmd } from './command/save-user-module-spent-duration.cmd';
import { UpdateCoachCmd } from './command/update-coach.cmd';
import { UpdateCoacheeObserversCmd } from './command/update-coachee-observers.cmd';
import { UpdateUserAvailabilitiesCmd } from './command/update-user-availabilities.cmd';
import { UpdateUserBiographyCmd } from './command/update-user-biography.cmd';
import { UpdateUserSettingsCmd } from './command/update-user-settings.cmd';
import { UpdateUserCmd } from './command/update-user.cmd';
import { UserRepositoryPort } from './port/user-repository.port';
import { FetchUserModulePushesQuery } from './query/module/push/fetch-user-module-pushes.query';

@Injectable()
export class UserApplicationAdapter
	extends ApplicationAdapter
	implements UserApplicationPort
{
	constructor(
		translateService: TranslateService,
		private _userApiPort: UserApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _userSessionPort: UserSessionPort,
		private _userAnalyticsPort: UserAnalyticsPort,
		private _userConverterService: UserConverterService,
		private _documentConverterPort: DocumentConverterPort,
		private _documentRepositoryPort: DocumentRepositoryPort,
		private _userNoteRepositoryPort: UserNoteRepositoryPort,
		private _userModuleConverterService: UserModuleConverterService,
		private _userAnswerRepositoryPort: UserModuleAnswerRepositoryPort,
		private _userModuleRepositoryPort: UserModuleRepositoryPort,
		private _organizationConvertPort: OrganizationConverterPort,
		private _userNoteConverterService: UserNoteConverterService,
		private _userSettingsConverterService: UserSettingsConverterService,
		private _organizationRepositoryPort: OrganizationRepositoryPort,
		private _userModulePushConverterService: UserModulePushConverterService,
		private _userEmailModelConverterService: UserEmailModelConverterService,
		private _moduleQuestionConverterPort: ModuleQuestionConverterPort,
		private _moduleQuestionRepositoryPort: ModuleQuestionRepositoryPort,
		private _userEmailModelRepositoryPort: UserEmailModelRepositoryPort,
		private _userModuleAnswerConverterService: UserModuleAnswerConverterService,
		private _userModuleSuggestionConverterService: UserModuleSuggestionConverterService,
		private _userModuleSuggestionRepositoryPort: UserModuleSuggestionRepositoryPort
	) {
		super(translateService);
	}

	private _commandHandled$ = new BehaviorSubject<
		CommandEvent<UserApplicationPort>
	>({
		timestamp: Date.now(),
		entityId: null,
		method: null,
	});

	public get commandHandled$(): Observable<CommandEvent<UserApplicationPort>> {
		return this._commandHandled$;
	}

	private get _userRepositoryPort(): UserRepositoryPort {
		return AppLocator.inject<UserRepositoryPort>(UserRepositoryPort);
	}

	public mustAnswerOnBoardingModule(): Observable<boolean> {
		return this._userApiPort.mustAnswerOnBoardingModule();
	}

	public mustAnswerFirstCoachingPlanModule(): Observable<boolean> {
		return this._userApiPort.mustAnswerFirstCoachingPlanModule();
	}

	public fetchStatisticsUser(): Observable<StatisticsUserOutput> {
		return this._userApiPort.fetchStatisticsUser();
	}

	public observeUserModuleSuggestions(): Observable<IUserModuleSuggestion[]> {
		return this.observeSessionUserId().pipe(
			filterNil(),
			switchMap((userId) =>
				this._userModuleSuggestionRepositoryPort.observeUserModuleSuggestions(
					userId
				)
			)
		);
	}

	public markMoodDescriptionShown(): Observable<void | Error> {
		return this._userApiPort.markMoodDescriptionShown().pipe(
			switchMap(() => this.fetchUser()),
			catchError((e) => of(e))
		);
	}

	public fetchUserModuleSuggestions(): Observable<void | Error> {
		return this.observeSessionUserId().pipe(
			take(1),
			switchMap((userId) =>
				this._userApiPort.fetchUserModuleSuggestions().pipe(
					tap((suggestions) =>
						this._userModuleSuggestionRepositoryPort.replaceUserModuleSuggestions(
							userId,
							this._userModuleSuggestionConverterService.userModuleSuggestionsAsEntities(
								suggestions
							)
						)
					),
					mapTo(void 0),
					catchError((e) => of(e))
				)
			)
		);
	}

	public isUserNewsletterEnabled(): Observable<boolean> {
		return this._userApiPort.isUserNewsletterEnabled();
	}

	public validateUserMfa(code: string): Observable<void> {
		return this._userApiPort.validateUserMfa(code).pipe(
			switchMap(() => this._userApiPort.fetchUserByJwToken()),
			switchMap((user) =>
				this.fetchUserModulePushes({ userId: user.id }).pipe(
					catchErrorTo([] as UserModulePushOutput[]),
					map((modulePushes) => this._saveUsers([user], modulePushes))
				)
			),
			mapTo(void 0)
		);
	}

	public updateUserGrantNewsLetter(grantNewsLetter: boolean): Observable<void> {
		return this._userApiPort.updateUserGrantNewsLetter(grantNewsLetter);
	}

	public generateUserMfa(): Observable<UserMfaEngageOutput> {
		return this._userApiPort.generateUserMfa();
	}

	public disableUserMfa(): Observable<void> {
		return this._userApiPort.disableUserMfa().pipe(
			switchMap(() => this._userApiPort.fetchUserByJwToken()),
			switchMap((user) =>
				this.fetchUserModulePushes({ userId: user.id }).pipe(
					catchErrorTo([] as UserModulePushOutput[]),
					map((modulePushes) => this._saveUsers([user], modulePushes))
				)
			),
			mapTo(void 0)
		);
	}

	public observeUserPlanChanges(): Observable<
		Notification<NotificationDomain, null>
	> {
		return this._appPushApiPort.observeDomainMessage<null>(
			NotificationDomain.USER_PLAN
		);
	}

	public observeUserStatisticsChanges(): Observable<
		Notification<NotificationDomain, null>
	> {
		return this._appPushApiPort.observeDomainMessage<null>(
			NotificationDomain.USER_STATISTICS
		);
	}

	public observeWebsocketChanges(): Observable<boolean> {
		return this._appPushApiPort.observeWebsocketChanges();
	}

	public observeUserModuleSuggestionsChanges(): Observable<
		Notification<NotificationDomain, string>
	> {
		return this._appPushApiPort.observeDomainMessage<string>(
			NotificationDomain.USER_MODULE_SUGGESTIONS
		);
	}

	public observeUserModuleMetadataGenerated(): Observable<
		Notification<NotificationDomain, string>
	> {
		return this._appPushApiPort.observeDomainMessage<string>(
			NotificationDomain.USER_MODULE_METADATA
		);
	}

	public observeUserModulePushUpdated(): Observable<
		Notification<NotificationDomain, string>
	> {
		return this._appPushApiPort.observeDomainMessage<string>(
			NotificationDomain.USER_MODULE_PUSH
		);
	}

	public observeUserExternalEventTaskChanges(): Observable<
		Notification<NotificationDomain, string>
	> {
		return this._appPushApiPort.observeDomainMessage<string>(
			NotificationDomain.EXTERNAL_EVENT_TASK
		);
	}

	public disassociateCoachWithCoachee(coacheeId: string): Observable<void> {
		return this._userApiPort.disassociateCoachWithCoachee(coacheeId).pipe(
			tap(() => this._userRepositoryPort.removeUserCoach(coacheeId)),
			mapTo(void 0)
		);
	}

	public fetchUserSettings(): Observable<IUserSettings> {
		return this.observeSessionUser().pipe(
			filterNil(),
			pluck('id'),
			distinctUntilChanged(),
			switchMap((userId) => this._userApiPort.fetchMessagingUserSettings(userId)),
			map((settings) =>
				this._userSettingsConverterService.userSettingsAsEntity(settings)
			)
		);
	}

	public createUserNote(cmd: CreateUserNoteCmd): Promise<void> {
		return from(this._userApiPort.createUserNote(cmd))
			.pipe(
				switchMap((userNoteId) =>
					this._userApiPort
						.ownedUserNotes(cmd.targetId)
						.pipe(map((notes) => notes.find((note) => note.id === userNoteId)))
				),
				tap((note) => this._saveUserNotes([note])),
				mapTo(null)
			)
			.toPromise();
	}

	public associateCoachWithCoachee(
		cmd: AssociateCoachWithCoacheeCmd
	): Observable<void> {
		return from(this._userApiPort.associateCoachWithCoachee(cmd)).pipe(
			tap((user) => this._saveUsers([user])),
			mapTo(null)
		);
	}

	public deleteUserNote(noteId: string): Promise<void> {
		return from(this._userApiPort.deleteUserNote(noteId))
			.pipe(
				tap(() => this._userNoteRepositoryPort.deleteNoteById(noteId)),
				mapTo(null)
			)
			.toPromise();
	}

	public fetchUserModulePushes(
		query: FetchUserModulePushesQuery
	): Observable<UserModulePush[]> {
		return this._userApiPort
			.fetchUserModulePushes(query)
			.pipe(
				map((modulePushes) =>
					this._userModulePushConverterService.userModulePushesAsEntities(
						modulePushes
					)
				)
			);
	}

	public observeAttachedOrganizationsByIds(
		ids: string[]
	): Observable<Organization[]> {
		const remote$ = this._userApiPort.fetchOrganizationsByIds(ids).pipe(
			switchTap((organizations) =>
				this._organizationRepositoryPort.saveOrganizations(
					this._organizationConvertPort.organizationsAsEntities(organizations)
				)
			),
			switchMapTo(this._organizationRepositoryPort.observeOrganizationByIds(ids))
		);

		return this._organizationRepositoryPort
			.observeOrganizationByIds(ids)
			.pipe(
				switchMap((organizations) =>
					organizations.length === ids.length ? of(organizations) : remote$
				)
			);
	}

	public createUser(cmd: CreateUserCmd): Promise<void> {
		return from(this._userApiPort.createUser(cmd))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateCoacheeObservers(
		cmd: UpdateCoacheeObserversCmd
	): Observable<void> {
		return this._userApiPort.updateCoacheeObservers(cmd).pipe(
			tap((user) => this._saveUsers([user])),
			mapTo(null)
		);
	}

	public addModuleToUserPlan(cmd: AddModuleToUserPlanCmd): Promise<void> {
		return from(this._userApiPort.addModuleToPlan(cmd.userId, cmd.moduleId))
			.pipe(
				switchMap(() => this.fetchUserModules(cmd.userId)),
				tap((userModules) =>
					this._userModuleRepositoryPort.saveUserModules(
						this._userModuleConverterService.userModulesAsEntities(userModules)
					)
				),
				mapTo(void 0)
			)
			.toPromise();
	}

	public resendUserModulePush(cmd: ResendUserModulePushCmd): Promise<void> {
		return from(this._userApiPort.resendUserModulePush(cmd))
			.pipe(
				map((mps) =>
					this._userModulePushConverterService.userModulePushesAsEntities(mps)
				),
				switchMap((modulePushes) =>
					this._userRepositoryPort
						.findById(cmd.userId)
						.then((user) => user.updateModulePushes(modulePushes))
				),
				tap((user) => this._userRepositoryPort.saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public associateUserWithCoach(coachId: string): Promise<void> {
		return from(this._userApiPort.associateUserWithCoach(coachId))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public closeUserSession(): void {
		this._userSessionPort.closeSession();
	}

	public deleteModuleFromUserPlan(
		cmd: DeleteModuleFromUserPlanCmd
	): Promise<void> {
		return from(this._userApiPort.deleteModuleFromPlan(cmd.userId, cmd.moduleId))
			.pipe(
				tap((userModule) =>
					this._userModuleRepositoryPort.removeUserModule(cmd.userId, cmd.moduleId)
				),
				mapTo(null)
			)
			.toPromise();
	}

	public deleteUserModuleAnswers(
		cmd: DeleteUserModuleAnswersCmd
	): Promise<void> {
		return from(
			this._userApiPort.deleteUserModuleAnswers(cmd.userId, cmd.moduleId)
		)
			.pipe(
				tap((answersId) =>
					this._userAnswerRepositoryPort.removeUserAnswersByIds(answersId)
				),
				mapTo(null)
			)
			.toPromise();
	}

	public fetchUserAvailableSlots(
		query: FetchUserAvailableSlotsQuery
	): Promise<UserAvailableSlotOutput[]> {
		return this._userApiPort.fetchUserAvailableSlots(query);
	}

	public fetchUserNetworkInfo(
		auth0UserId: string
	): Promise<Auth0ConnectionOutput> {
		return this._userApiPort.fetchUserNetworkInfo(auth0UserId);
	}

	public fetchUserPhasesStats(userId: string): Promise<UserPhaseStatsOutput[]> {
		return this._userApiPort.fetchUserPhasesStats(userId);
	}

	public observeSearchedCoachesIncludingPartners(
		query: ObserveSearchedCoachesIncludingPartnersQuery
	): Observable<RemoteData<IUser[]>> {
		const api$ = from(
			this._userApiPort.searchCoachesIncludingPartners(query)
		).pipe(
			tap((page) => this._saveUsers(page.data)),
			map((page) => ({ ...page, fresh: true } as RemoteData<UserOutput[]>)),
			catchError((e) =>
				of({
					fresh: true,
					data: [],
					error: e,
				} as RemoteData<UserOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<UserOutput[]>)
		);

		const repo$ =
			this._userRepositoryPort.observeSearchedCoachesIncludingPartners(query);

		return appEntities(api$, repo$);
	}

	public fetchUser(): Observable<void | Error> {
		return this._userApiPort.fetchUserByJwToken().pipe(
			tap((user) => this._saveUsers([user])),
			catchError((e) => of(e))
		);
	}

	public markUserHelpConversationAsShown() {
		this._userAnalyticsPort.markUserConversationAsShown();
	}

	@CacheObservable()
	public observeSessionUser(): Observable<IUser> {
		return this._userSessionPort.observeJwToken().pipe(
			switchMap((jwt) =>
				jwt
					? from(this._userApiPort.fetchUserByJwToken()).pipe(
							switchMap((user) =>
								this.fetchUserModulePushes({ userId: user.id }).pipe(
									catchErrorTo([] as UserModulePushOutput[]),
									switchMap((modulePushes) =>
										from(this._saveUsers([user], modulePushes)).pipe(
											switchMap(() => this._userRepositoryPort.observeUserById(user.id))
										)
									)
								)
							),
							switchMap((user) =>
								(user?.organization$ || of(null as Organization)).pipe(
									tap(
										(organization) =>
											user &&
											organization &&
											(organization.isPropertyTrue(
												OrganizationPropertyName.IS_SUBSCRIPTION_OPTIONAL,
												true
											) ||
												organization.paymentProviderSubscriptionId) &&
											this._userAnalyticsPort.identifyUserForAnalytics({
												userLanguage:
													user?.metadata?.language ?? this._translateService.currentLang,
												userOrganizationColor: organization?.primaryColor,
												userOrganizationName: organization?.name,
												userOrganizationId: organization?.id,
												userIntercomHash: user.intercomHash,
												userEmailAddress: user.emailAddress,
												userName: user.name,
												userRole: user.role,
												userId: user.id,
											})
									),
									mapTo(user)
								)
							),
							catchError((_) => {
								this._userSessionPort.closeSession();
								return of(null);
							})
					  )
					: of(null)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public fetchModulesStatistic(): Observable<UserModuleStatisticOutput[]> {
		return this._userApiPort.fetchModulesStatistic();
	}

	public fetchUserConnectedDaysCount(): Observable<number> {
		return this._userApiPort.fetchUserConnectedDaysCount();
	}

	@CacheObservable()
	public observeSessionUserId(): Observable<string> {
		return this.observeSessionUser().pipe(
			map((user) => user?.id),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	@CacheObservable()
	public observeSessionOrganizationId(): Observable<string> {
		return this.observeSessionUser().pipe(
			map((user) => user?.organizationId),
			distinctUntilChanged(),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	public generateUserCV(cmd: GenerateUserCVCmd): Observable<string> {
		return this._userApiPort
			.generateUserCV(cmd)
			.pipe(catchError((_) => of(null)));
	}

	public observeUserAnswersByModulesId(
		query: ObserveUserAnswersByModulesIdQuery
	): Observable<RemoteData<IUserModuleAnswer[]>> {
		const api$ = from(
			this._userApiPort.fetchUserModuleAnswersByModulesId(query)
		).pipe(
			tap((userAnswers) => this._saveUserAnswers(userAnswers)),
			map(
				(userAnswers) =>
					({
						data: userAnswers,
						fresh: true,
					} as RemoteData<UserModuleAnswerOutput[]>)
			),
			catchError((_) =>
				of({ fresh: true, data: [] } as RemoteData<UserModuleAnswerOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<UserModuleAnswerOutput[]>)
		);

		const repo$ =
			this._userAnswerRepositoryPort.observeUserAnswersByModulesId(query);

		return appEntities(api$, repo$);
	}

	public observeUserAnswersByQuestionsId(
		query: ObserveUserAnswersByQuestionsIdQuery
	) {
		const api$ = from(
			this._userApiPort.fetchUserAnswersByQuestionsId(query)
		).pipe(
			tap((userAnswers) => this._saveUserAnswers(userAnswers)),
			map(
				(userAnswers) =>
					({
						data: userAnswers,
						fresh: true,
					} as RemoteData<UserModuleAnswerOutput[]>)
			),
			catchError((_) =>
				of({
					fresh: true,
					data: [],
				} as RemoteData<UserModuleAnswerOutput[]>)
			),
			startWith({
				fresh: false,
				data: [],
			} as RemoteData<UserModuleAnswerOutput[]>)
		);

		const repo$ = this._userAnswerRepositoryPort.observeUserAnswersByQuestionsId(
			query.userId,
			query.questionsId
		);

		return appEntities(api$, repo$);
	}

	public observeUserById(id: string): Data$<IUser> {
		const api$ = combineLatest([
			this._userApiPort.fetchUserById(id),
			this.fetchUserModulePushes({ userId: id }).pipe(
				catchErrorTo([] as UserModulePushOutput[])
			),
		]).pipe(
			tap(([user, userModulePushesOutput]) =>
				this._saveUsers([user], userModulePushesOutput)
			),
			map(([user]) => ({ data: user, fresh: true } as RemoteData<UserOutput>)),
			catchError(() => of({ fresh: true, data: null } as RemoteData<UserOutput>)),
			startWith({ fresh: false, data: null } as RemoteData<UserOutput>)
		);

		const repo$ = this._userRepositoryPort.observeUserById(id);

		return appEntity(api$, repo$);
	}

	public observeUserByIds(ids: string[]): Data$<IUser[]> {
		const api$ = from(this._userApiPort.fetchUsersByIds(ids)).pipe(
			tap((users) => this._saveUsers(users)),
			map((users) => ({ data: users, fresh: true } as RemoteData<UserOutput[]>)),
			catchError(() =>
				of({ fresh: true, data: null } as RemoteData<UserOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<UserOutput[]>)
		);

		const repo$ = this._userRepositoryPort.observeUserByIds(ids);

		return appEntities(api$, repo$);
	}

	public observeCoacheesWithoutCoach(ids: string[]): Data$<IUser[]> {
		const api$ = from(this._userApiPort.fetchCoacheesWithoutCoach(ids)).pipe(
			switchMap((users) => this._saveUsers(users)),
			switchMap((users) =>
				this._userRepositoryPort.observeUserByIds(users.map((user) => user.id))
			),
			map(
				(users) =>
					({
						fresh: true,
						data: users,
					} as RemoteData<User[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<User[]>)
		);
		const repo$ = this._userRepositoryPort.observeCoacheesWithoutCoach(ids);

		return appEntities(api$, repo$);
	}

	public fetchUserModules(userId: string): Observable<UserModuleOutput[]> {
		return from(this._userApiPort.fetchUserModulesByUserId(userId));
	}

	public observeUserModules(
		userId: string
	): Observable<RemoteData<IUserModule[]>> {
		const api$ = from(this._userApiPort.fetchUserModulesByUserId(userId)).pipe(
			tap((userModules) =>
				this._userModuleRepositoryPort.saveUserModules(
					this._userModuleConverterService.userModulesAsEntities(userModules),
					userId
				)
			),
			map(
				(userModules) =>
					({
						data: userModules,
						fresh: true,
					} as RemoteData<UserModuleOutput[]>)
			),
			catchError(() =>
				of({ fresh: true, data: [] } as RemoteData<UserModuleOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<UserModuleOutput[]>)
		);

		const repo$ =
			this._userModuleRepositoryPort.observeUserModulesByUserId(userId);

		return appEntities(api$, repo$);
	}

	public observeCoachCoachees(): Observable<RemoteData<IUser[]>> {
		const api$ = from(this._userApiPort.fetchCoachCoachees()).pipe(
			switchMap((users) => this._saveUsers(users)),
			map(
				(users) =>
					({
						fresh: true,
						data: users,
					} as RemoteData<UserOutput[]>)
			),
			startWith({ fresh: false, data: [] } as RemoteData<UserOutput[]>)
		);

		const repo$ = this.observeSessionUserId().pipe(
			switchMap((userId) => this._userRepositoryPort.observeUsersByCoachId(userId))
		);

		return appEntities(api$, repo$);
	}

	public fetchUserEmailModels(): Observable<string[] | Error> {
		return this._userApiPort.fetchUserEmailModels().pipe(
			tap((models) =>
				this._userEmailModelRepositoryPort.saveUserEmailModels(
					this._userEmailModelConverterService.userEmailModelsAsEntities(models)
				)
			),
			map((models) => models.map((model) => model.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public observeUserEmailModels(): Observable<IUserEmailModel[]> {
		return this.observeSessionUserId().pipe(
			switchMap((userId) =>
				this._userEmailModelRepositoryPort.observeUserEmailModels(userId)
			)
		);
	}

	public saveUserAnswers(cmd: SaveUserAnswersCmd): Promise<void> {
		return from(this._userApiPort.saveUserAnswers(cmd))
			.pipe(
				tap((answers) => this._saveUserAnswers(answers)),
				switchMap(() =>
					cmd.isAutoSave
						? this._markUserModuleStarted(cmd.userId, cmd.moduleId)
						: this._markUserModuleDone(cmd.userId, cmd.moduleId)
				),
				tap(
					() =>
						!cmd.isAutoSave &&
						this._commandHandled$.next({
							method: 'saveUserAnswers',
							timestamp: Date.now(),
							entityId: cmd.userId,
						})
				),
				mapTo(null)
			)
			.toPromise();
	}

	public updateCoach(cmd: UpdateCoachCmd): Promise<void> {
		return from(this._userApiPort.updateCoach(cmd))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateCoachBiography(cmd: UpdateUserBiographyCmd): Promise<void> {
		return from(this._userApiPort.updateCoachBiography(cmd))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUser(cmd: UpdateUserCmd): Promise<void> {
		return from(this._userApiPort.updateUser(cmd))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserSettings(
		cmd: UpdateUserSettingsCmd
	): Observable<IUserSettings> {
		return this._userApiPort
			.updateMessagingUserSettings(cmd)
			.pipe(
				map((settings) =>
					this._userSettingsConverterService.userSettingsAsEntity(settings)
				)
			);
	}

	public updateUserAvailabilities(
		cmd: UpdateUserAvailabilitiesCmd
	): Promise<void> {
		return from(this._userApiPort.updateUserAvailabilities(cmd))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserAvatarUrl(file: File): Promise<void> {
		return from(this._userApiPort.generateUserAvatarUrl(file))
			.pipe(
				switchMap((avatarUrl) => this._userApiPort.updateUserAvatar(avatarUrl)),
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserLanguage(lang: string): Promise<IUser> {
		return this._userApiPort
			.updateUserLanguage(lang)
			.then((user) => {
				this._commandHandled$.next({
					method: 'updateUserLanguage',
					timestamp: Date.now(),
					entityId: user.id,
				});
				return user;
			})
			.then((user) => this._saveUsers([user]))
			.then((users) => this._userConverterService.userAsEntity(users[0], []));
	}

	public updateUserModule(cmd: UpdateUserModuleCmd): Promise<void> {
		return from(this._userApiPort.updateUserModule(cmd))
			.pipe(
				tap((userModule) =>
					this._userModuleRepositoryPort.saveUserModules([
						this._userModuleConverterService.userModuleAsEntity(userModule),
					])
				),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserModuleCoachComment(
		cmd: UpdateUserModuleCoachCommentCmd
	): Promise<void> {
		return from(this._userApiPort.updateUserModuleCoachComment(cmd))
			.pipe(
				tap((userModule) =>
					this._userModuleRepositoryPort.saveUserModules([
						this._userModuleConverterService.userModuleAsEntity(userModule),
					])
				),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserModuleRating(cmd: UpdateUserModuleRatingCmd): Promise<void> {
		return from(this._userApiPort.updateUserModuleRating(cmd))
			.pipe(
				tap((userModule) =>
					this._userModuleRepositoryPort.saveUserModules([
						this._userModuleConverterService.userModuleAsEntity(userModule),
					])
				),
				mapTo(null)
			)
			.toPromise();
	}

	public updateUserQuote(quote: string): Promise<void> {
		return from(this._userApiPort.updateUserQuote(quote))
			.pipe(
				tap((user) => this._saveUsers([user])),
				mapTo(null)
			)
			.toPromise();
	}

	public createUserModuleAnswersDocument(
		cmd: CreateUserModuleAnswersDocumentCmd
	): Observable<void> {
		return from(this._userApiPort.createUserModuleAnswersDocument(cmd)).pipe(
			mapTo(null)
		);
	}

	public generateUserModuleAnswers(
		cmd: GenerateUserModuleAnswersCmd
	): Observable<string> {
		return from(this._userApiPort.generateUserModuleAnswers(cmd));
	}

	public saveUsers(ids: string[]): Promise<void> {
		return from(this._userApiPort.fetchUsersByIds(ids))
			.pipe(
				map((users) => this._userConverterService.usersAsEntities(users)),
				switchMap((users) => this._userRepositoryPort.saveUsers(users))
			)
			.toPromise();
	}

	public observeNotes(ids: string[]): Observable<IUserNote[]> {
		return this._userNoteRepositoryPort.observeNotes(ids);
	}

	public SearchUserCoachNotes(
		query: ObserveSearchedUserCoachNotesQuery
	): Observable<string[] | Error> {
		return this._userApiPort.searchUserCoachNotes(query).pipe(
			tap((notes) => this._saveUserNotes(notes)),
			map((notes) => notes.map((note) => note.id)),
			catchError((e) => of(Error(e)))
		);
	}

	public saveUserModuleSpentDuration(
		cmd: SaveUserModuleSpentDurationCmd
	): Observable<void> {
		return this._userApiPort.saveUserModuleSpentDuration(cmd);
	}

	private async _markUserModuleDone(
		userId: string,
		moduleId: string
	): Promise<void> {
		const userModule = this._userModuleRepositoryPort.findByUserIdAndModuleId(
			userId,
			moduleId
		);
		userModule?.markDone();

		return this._userModuleRepositoryPort.saveUserModules([userModule]);
	}

	private async _markUserModuleStarted(
		userId: string,
		moduleId: string
	): Promise<void> {
		const userModule = this._userModuleRepositoryPort.findByUserIdAndModuleId(
			userId,
			moduleId
		);
		userModule?.markStarted();

		return this._userModuleRepositoryPort.saveUserModules([userModule]);
	}

	private _saveUserNotes(notes: UserNoteOutput[]): Promise<void> {
		return this._userNoteRepositoryPort.persistUserNotes(
			this._userNoteConverterService.userNotesAsEntities(notes)
		);
	}

	private _saveUserAnswers(
		userAnswers: UserModuleAnswerOutput[]
	): Promise<void> {
		return Promise.all([
			this._userAnswerRepositoryPort.saveUserAnswers(
				userAnswers.map((userAnswer) =>
					this._userModuleAnswerConverterService.userModuleAnswerAsEntity(userAnswer)
				)
			),
			this._moduleQuestionRepositoryPort.saveModuleQuestions(
				uniqBy(
					userAnswers.map((answer) => answer.question).filter((q) => !!q),
					'id'
				).map((question) =>
					this._moduleQuestionConverterPort.asModuleQuestion(question)
				)
			),
			this._documentRepositoryPort.saveDocuments(
				uniqBy(
					userAnswers.map((answer) => answer.document).filter((q) => !!q),
					'id'
				).map((document) => this._documentConverterPort.documentAsEntity(document))
			),
		]).then((_) => null);
	}

	private _saveUsers(
		users: UserOutput[],
		userModulePushes: UserModulePushOutput[] = []
	): Promise<UserOutput[]> {
		const organizations = uniqBy(
			users.map((user) => user.organization).filter((o) => !!o),
			'id'
		);

		return Promise.all([
			this._userRepositoryPort.saveUsers(
				users.map((user) =>
					this._userConverterService.userAsEntity(user, userModulePushes)
				)
			),
			this._organizationRepositoryPort.saveOrganizations(
				organizations.map((organization) =>
					this._organizationConvertPort.organizationAsEntity(organization)
				)
			),
			this._userRepositoryPort.saveUsers(
				uniqBy(
					users.map((user) => user.coach).filter((c) => !!c),
					'id'
				).map((coach) => this._userConverterService.userAsEntity(coach))
			),
		]).then(() => users);
	}

	public fetchUserDaysUntilNextLevelUpCount(): Observable<number> {
		return this._userApiPort.fetchUserDaysUntilNextLevelUpCount();
	}

	public reconnectToUserNetwork(
		cmd: ReconnectToUserNetworkCmd
	): Observable<void> {
		return this._userApiPort.reconnectToUserNetwork(cmd).pipe(
			tap(() =>
				this._commandHandled$.next({
					method: 'reconnectToUserNetwork',
					timestamp: Date.now(),
					entityId: null,
				})
			)
		);
	}

	public connectToUserNetwork(cmd: ConnectToUserNetworkCmd): Observable<void> {
		return this._userApiPort.connectToUserNetwork(cmd).pipe(
			tap(() =>
				this._commandHandled$.next({
					method: 'connectToUserNetwork',
					timestamp: Date.now(),
					entityId: null,
				})
			)
		);
	}

	public disconnectFromUserNetwork(
		networkType: UserNetworkType
	): Observable<void> {
		return this._userApiPort.disconnectFromUserNetwork(networkType).pipe(
			tap(() =>
				this._commandHandled$.next({
					method: 'disconnectFromUserNetwork',
					timestamp: Date.now(),
					entityId: null,
				})
			)
		);
	}

	public userNetworkState(
		networkType: UserNetworkType
	): Observable<UserNetworkState> {
		return this._userApiPort.userNetworkState(networkType);
	}

	public synchronizeUserNetworkSelectedCalendars(
		cmd: SynchronizeUserNetworkSelectedCalendarsCmd
	): Observable<void> {
		return this._userApiPort.synchronizeUserNetworkSelectedCalendars(cmd);
	}

	public userExternalCalendar(
		networkType: UserNetworkType
	): Observable<ExternalCalendarInformationOutput[]> {
		return this._userApiPort.userExternalCalendar(networkType);
	}

	public markUserModuleDescriptionAsVisualized(
		cmd: MarkUserModuleDescriptionAsVisualizedCmd
	): Observable<void> {
		return this._userApiPort
			.markUserModuleDescriptionAsVisualized(cmd.coachingPlanModuleId)
			.pipe(
				switchMap((userModule) => this.fetchUserModules(cmd.userId)),
				tap((userModules) =>
					this._userModuleRepositoryPort.saveUserModules(
						this._userModuleConverterService.userModulesAsEntities(userModules)
					)
				),
				mapTo(void 0)
			);
	}

	public countUserModulePushesByStatusApiQuery(userId: string): Observable<{
		[key: string]: number;
	}> {
		return this._userApiPort.countUserModulePushesByStatusApiQuery(userId);
	}

	public generateUserPushModuleAnswersSynthesis(
		coacheeId: string
	): Observable<void> {
		return this._userApiPort
			.generateUserPushModuleAnswersSynthesis(coacheeId)
			.pipe(
				tap(() =>
					this._commandHandled$.next({
						method: 'generateUserPushModuleAnswersSynthesis',
						timestamp: Date.now(),
						entityId: null,
					})
				),
				mapTo(void 0)
			);
	}

	public ownedUserModulePushes(
		query: FetchOwnedUserModulePushesQuery
	): Observable<MessagingUserModulePushOutput[]> {
		return this._userApiPort.ownedUserModulePushes(query);
	}
}
